<template>
  <!-- Live preview of the brand kit (spec 2026-09-30): one magazine page built
       from the same resolvers the emitter uses. A click on a part asks the kit
       to open the matching tile (`waehle`). -->
  <div class="ci-bv" data-test="ci-brand-vorschau">
    <typo-buehne
      :geraet.sync="geraet"
      :breiten="{ desktop: 1200, tablet: 768, handy: 390 }"
      :max-zoom="1"
      test-prefix="ci-brand"
    >
      <div class="bv-seite" :class="`bv-${geraet}`" :style="seitenStil">
        <div class="bv-kopf">
          <button type="button" class="bv-ziel bv-marke" data-test="bv-logo" :aria-label="t('kachel.logo')" @click="waehle('logo')">
            <img v-if="faviconUrl" :src="faviconUrl" alt="" class="bv-favicon">
            <span v-else class="bv-favicon bv-favicon-leer">W</span>
            <img v-if="logoUrl" :src="logoUrl" alt="" class="bv-logo">
            <span v-else class="bv-logo bv-logo-leer">{{ t('logoPlatzhalter') }}</span>
          </button>
          <span class="bv-navi" :style="rolle('caption')">{{ t('vNavi') }}</span>
        </div>
        <div class="bv-inhalt">
          <!-- Every typography role once, in page order (feedback 30.09.2026).
               The kicker and the tag show the second brand colour. -->
          <button type="button" class="bv-ziel bv-kicker" data-test="bv-kicker" :aria-label="t('kachel.farben')" @click="waehle('farben')">
            <span :style="rolle('caption')">{{ t('vKicker') }}</span>
          </button>
          <button
            v-for="r in ['display', 'h1', 'lead', 'h2', 'body']"
            :key="r"
            type="button"
            v-bind="textZiel(r)"
            @click="waehle('typo', r)"
          ><span :style="rolle(r)">{{ muster(r) }}</span></button>
          <figure class="bv-figur">
            <button type="button" class="bv-ziel bv-bild bv-bild-gross" data-test="bv-bild" :aria-label="t('kachel.formen')" @click="waehle('formen')" />
            <button type="button" v-bind="textZiel('caption')" @click="waehle('typo', 'caption')">
              <span class="bv-leise" :style="rolle('caption')">{{ muster('caption') }}</span>
            </button>
          </figure>
          <div class="bv-karten">
            <div class="bv-karte">
              <button type="button" class="bv-ziel bv-bild" data-test="bv-karte" :aria-label="t('kachel.formen')" @click="waehle('formen')" />
              <button type="button" class="bv-ziel bv-tag" data-test="bv-tag" :aria-label="t('kachel.farben')" @click="waehle('farben')">
                {{ t('vTag') }}
              </button>
              <button
                v-for="r in ['h3', 'h4']"
                :key="r"
                type="button"
                v-bind="textZiel(r)"
                @click="waehle('typo', r)"
              ><span :style="rolle(r)">{{ muster(r) }}</span></button>
              <!-- CI link style (Lesbarkeit §6.4): the underline as in Bereiche. -->
              <span class="bv-link" data-test="bv-link" :style="rolle('body')">{{ t('vLink') }}</span>
            </div>
            <!-- The box holds role buttons, so it cannot be a button itself;
                 its shapes are reachable by keyboard through the card image. -->
            <div class="bv-box" data-test="bv-box" @click.self="waehle('formen')">
              <button
                v-for="r in ['h5', 'h6']"
                :key="r"
                type="button"
                v-bind="textZiel(r)"
                @click="waehle('typo', r)"
              ><span :style="rolle(r)">{{ muster(r) }}</span></button>
            </div>
          </div>
          <div>
            <!-- In typography mode the button is the "button" role, else the tile "Knöpfe". -->
            <button
              type="button"
              class="bv-ziel bv-knopf"
              :class="{ aktiv: aktiveRolle === 'button' }"
              data-test="bv-knopf"
              :aria-label="aktiveRolle ? rollenLabel('button') : t('kachel.knopf')"
              @click="knopfKlick"
            ><span :style="rolle('button')">{{ muster('button') }}</span></button>
          </div>
        </div>
      </div>
    </typo-buehne>
    <!-- Text on brand colours follows the AA guard of Bereiche (spec 2026-10-03 Lesbarkeit M5). -->
    <p class="bv-hinweis" data-test="bv-hinweis">{{ t('vorschauHinweis') }}</p>
  </div>
</template>

<script>
import { resolveDesignTokenPreview } from '@/editor-v2/utils/ciTokens';
import TypoBuehne from '@/components/settings/TypoBuehne.vue';
import { vorschauStil } from '@/components/settings/typoTabelle';
import { vorschauStil as farbStil } from './ciFarbenV25';

export default {
  name: 'CiBrandVorschau',
  components: { TypoBuehne },
  props: {
    // corporate_identity: logo, favicon, colours, designTokens (incl. typography).
    ci: { type: Object, required: true },
    // The role the typography tile edits; null when that tile is closed.
    aktiveRolle: { type: String, default: null },
  },
  data: () => ({ geraet: 'desktop' }),
  computed: {
    typo() {
      return (this.ci.designTokens && this.ci.designTokens.typography) || {};
    },
    logoUrl() {
      return (this.ci.logo && this.ci.logo.image && this.ci.logo.image.url) || '';
    },
    faviconUrl() {
      return (this.ci.favicon && this.ci.favicon.image && this.ci.favicon.image.url) || '';
    },
    /** CSS variables of the page: colours as the colour preview, tokens as the emitter. */
    seitenStil() {
      const t = this.ci.designTokens || {};
      const w = resolveDesignTokenPreview(t);
      return {
        ...farbStil(this.ci),
        '--b-space-s': w.space.s,
        '--b-space-m': w.space.m,
        '--b-space-l': w.space.l,
        '--b-r-card': w.radius.card,
        '--b-r-image': w.radius.image,
        '--b-r-button': w.radius.button,
        '--b-r-box': w.radius.box,
        // CI card style (Lesbarkeit §6.2): a line in the line colour or flat instead of the shadow.
        '--b-shadow-card': t.cardStyle === 'linie' ? '0 0 0 1px var(--v-line)' : (t.cardStyle === 'flach' && 'none') || w.shadowCard,
        '--b-link-line': t.linkStyle === 'dezent' ? 'color-mix(in srgb, currentColor 35%, transparent)' : 'currentColor',
        '--b-btn-pad': w.buttonPadding,
        '--b-width': `${w.contentWidth}px`,
      };
    },
  },
  methods: {
    t(key) {
      return this.$t(`settings.ciBrandKit.${key}`);
    },
    rollenLabel(role) {
      return this.$t(`settings.project.corporateIdentity.typo.rolle.${role}`);
    },
    rolle(role) {
      return vorschauStil(role, this.typo, this.geraet);
    },
    muster(role) {
      return this.$t(`settings.project.corporateIdentity.typo.muster.${role}`);
    },
    textZiel(role) {
      return {
        class: ['bv-ziel', 'bv-text', { aktiv: this.aktiveRolle === role }],
        'data-test': `bv-text-${role}`,
        'aria-label': this.rollenLabel(role),
      };
    },
    knopfKlick() {
      if (this.aktiveRolle) this.waehle('typo', 'button');
      else this.waehle('knopf');
    },
    waehle(kachel, role) {
      this.$emit('waehle', { kachel, rolle: role || null });
    },
  },
};
</script>

<style scoped>
.bv-hinweis { margin: 8px 0 0; font-size: 12px; color: #6c757d; }
/* First: the reset of the click targets must not undo the rules below. */
.bv-ziel {
  all: unset; display: block; box-sizing: border-box; cursor: pointer; outline-offset: 4px;
}
.bv-ziel:hover { outline: calc(1px / var(--tv-zoom)) dashed #8a8d92; }
.bv-ziel.aktiv, .bv-ziel:focus-visible { outline: calc(2px / var(--tv-zoom)) solid #2f8f5b; }
.bv-seite {
  background: var(--v-page); color: var(--v-ink); min-height: 100%;
  display: flex; flex-direction: column;
}
.bv-kopf {
  display: flex; justify-content: space-between; align-items: center; gap: 16px;
  padding: 14px 24px; background: var(--v-surface); border-bottom: 3px solid var(--v-primary);
}
.bv-marke { display: flex; align-items: center; gap: 12px; }
.bv-favicon { width: 24px; height: 24px; border-radius: 5px; object-fit: contain; }
.bv-favicon-leer {
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--v-primary); color: var(--v-primary-text); font-weight: 700; font-size: 14px;
}
.bv-logo { max-height: 36px; max-width: 180px; object-fit: contain; }
.bv-logo-leer {
  display: inline-flex; align-items: center; justify-content: center; height: 32px; padding: 0 16px;
  border-radius: 4px; background: var(--v-primary); color: var(--v-primary-text);
  font-size: 12px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
}
.bv-navi { color: var(--v-muted); }
.bv-inhalt {
  box-sizing: border-box; width: 100%; max-width: var(--b-width); margin: 0 auto;
  padding: calc(var(--b-space-l) * 2) 24px; display: flex; flex-direction: column; gap: var(--b-space-l);
}
.bv-kicker > span {
  display: block; color: var(--v-secondary); font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
}
.bv-kicker { margin-bottom: calc(var(--b-space-l) * -0.5); }
.bv-text > span { display: block; color: var(--v-ink); overflow-wrap: break-word; }
.bv-text > .bv-leise { color: var(--v-muted); }
.bv-figur { margin: 0; display: flex; flex-direction: column; gap: var(--b-space-s); }
.bv-bild { height: 120px; background: var(--v-muted); opacity: 0.35; border-radius: var(--b-r-image); }
.bv-bild-gross { height: 260px; }
.bv-handy .bv-bild-gross { height: 160px; }
.bv-tag {
  display: inline-block; width: auto; align-self: flex-start; padding: 4px 10px; border-radius: 999px;
  background: var(--v-secondary); color: var(--v-secondary-text);
  font-size: 12px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
}
.bv-karten { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--b-space-m); align-items: start; }
.bv-handy .bv-karten { grid-template-columns: minmax(0, 1fr); }
.bv-karte {
  background: var(--v-surface); border-radius: var(--b-r-card); box-shadow: var(--b-shadow-card);
  padding: var(--b-space-l); display: flex; flex-direction: column; gap: var(--b-space-m);
}
.bv-link { align-self: flex-start; text-decoration: underline; text-decoration-color: var(--b-link-line); text-underline-offset: 0.2em; font-weight: 600; }
.bv-box {
  border: 1px solid var(--v-line); border-radius: var(--b-r-box); padding: var(--b-space-l);
  display: flex; flex-direction: column; gap: var(--b-space-s); cursor: pointer;
}
.bv-knopf {
  display: inline-block; width: auto; background: var(--v-primary); padding: var(--b-btn-pad);
  border-radius: var(--b-r-button);
}
.bv-knopf > span { color: var(--v-primary-text); }

</style>
